/**
 * Sentinel value that can be returned from a `TellescopeUpdaterFn` to signal that
 * the query should be invalidated (triggering a network refetch) rather than having
 * its cache updated in place.
 *
 * This is useful when an updater function determines at runtime that it cannot
 * construct a correct cache update (e.g. due to missing data or complex server-side
 * logic) and wants to fall back to a fresh fetch instead.
 *
 * @example
 * ```ts
 * useTellescopeMutation({
 *   model: "endusers",
 *   method: "updateOne",
 *   autoCacheUpdateStrategy: "update",
 *   cacheUpdateConfig: {
 *     endusers: {
 *       getSome: {
 *         updater: (old) => {
 *           if (!old) return FORCE_INVALIDATE;
 *           // ... update logic
 *         },
 *       },
 *     },
 *   },
 * });
 * ```
 */
export const FORCE_INVALIDATE = Symbol("FORCE_INVALIDATE");

/**
 * The type of the `FORCE_INVALIDATE` sentinel.
 * Use this when typing updater return values that may include a forced invalidation.
 */
export type ForceInvalidate = typeof FORCE_INVALIDATE;
